import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import Reveal from "@/components/Reveal";
import { API } from "@/lib/api";
import { INSIGHT_CATEGORIES } from "@/data/content";
import { useSEO } from "@/lib/useSEO";

export default function Insights() {
  useSEO("Legal Insights and Procedural Guidance | Smart Legal Solutions", "Factual legal explainers and procedural guides across criminal, civil, property, High Court, family, cyber, consumer and service matters.");
  const [articles, setArticles] = useState([]);
  const [category, setCategory] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      API.get("/insights", { params: { category, q: q || undefined } })
        .then((r) => setArticles(r.data))
        .catch(() => setArticles([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [category, q]);

  return (
    <div data-testid="insights-page" className="bg-warm pt-32">
      <div className="mx-auto max-w-7xl px-5 pb-24 lg:px-10">
        <Reveal>
          <p className="mono-label-dark">SLS Legal Intelligence</p>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl font-medium leading-[1.1] tracking-tight text-navy sm:text-5xl lg:text-6xl">
            Legal Insights and Procedural Guidance
          </h1>
          <div className="gold-hairline mt-8" />
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-navy/60">
            Clear information for informed decisions. Every article is informational only, carries a review date, and is
            not a substitute for advice on the facts of a particular matter.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-12">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative max-w-md flex-1">
              <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate" />
              <input
                data-testid="insights-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search topics, procedures, statutes…"
                className="field-input !pl-11"
                aria-label="Search legal insights"
              />
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-2.5" role="group" aria-label="Filter by category">
            {INSIGHT_CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`insights-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setCategory(c)}
                className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-300 ${category === c ? "border-navy bg-navy text-ivory" : "border-navy/25 text-navy/60 hover:border-gold hover:text-navy"}`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-14 grid gap-px bg-navy/10 md:grid-cols-2 lg:grid-cols-3">
          {loading &&
            Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-72 animate-pulse bg-ivory" />)}
          {!loading && articles.length === 0 && (
            <div className="col-span-full bg-ivory p-16 text-center" data-testid="insights-empty-state">
              <p className="font-serif text-2xl text-navy/60">No articles match the current filter.</p>
            </div>
          )}
          {!loading &&
            articles.map((a, i) => (
              <div key={a.slug} className="bg-ivory">
                <Link
                  to={`/legal-insights/${a.slug}`}
                  data-testid={`insight-card-${i}`}
                  className="group flex h-full flex-col p-8 transition-colors duration-500 hover:bg-warm"
                >
                  <div className="flex items-center justify-between">
                    <span className="mono-label-dark !text-gold">{a.category}</span>
                    <span className="font-mono text-[10px] tracking-[0.15em] text-slate">{a.reading_time} min</span>
                  </div>
                  <h2 className="mt-5 font-serif text-2xl font-medium leading-snug text-navy transition-colors duration-300 group-hover:text-gold">
                    {a.title}
                  </h2>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-navy/55">{a.excerpt}</p>
                  <div className="mt-8 border-t border-navy/10 pt-5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate">{a.author}</p>
                    <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-slate/70">Reviewed {a.date_reviewed}</p>
                  </div>
                </Link>
              </div>
            ))}
        </div>

        <Reveal className="mt-14">
          <p className="border-l-2 border-gold/50 pl-5 text-sm leading-relaxed text-navy/55">
            Articles are prepared for general legal awareness. They do not report confidential matters, do not use client
            information, and are reviewed before publication. A change in law does not automatically update published
            content; each article carries its own review date.
          </p>
        </Reveal>
      </div>
    </div>
  );
}
